<template>
  <v-dialog
    :value="enableDialog"
    scrollable
    persistent
    width="1200"
    @keydown.esc="closeButtonClickEvent"
    @close="closeButtonClickEvent"
  >
    <validation-observer
      ref="SalesItemPriceFormValidation"
    >
      <v-form>
        <v-card flat>
          <v-toolbar flat>
            <v-toolbar-title>{{ targetId ? '売上品目単価編集' : '売上品目単価追加' }}</v-toolbar-title>
          </v-toolbar>
          <v-divider />
          <v-card-text style="max-height: 60vh;overflow-y: auto">
            <v-row>
              <v-col cols="12" md="6">
                <cs-date-picker
                  :date.sync="forms.reference_date"
                  @dateChangedCallback="referenceDateChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      class="requiredMark"
                      rules="required"
                      name="改定年月日"
                    >
                      <v-text-field
                        v-model="forms.reference_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="改定年月日"
                        outlined
                        persistent-placeholder
                        placeholder="日付を選択"
                        readonly
                        style="font-size: 80%"
                        @click:clear="forms.reference_date = null"
                      >
                      </v-text-field>
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <v-checkbox
                  v-model="forms.apply_to_all"
                  dense
                  :label="forms.id ? '新規事業者追加時に利用可とする' : '全事業者に適用する'"
                  hide-details="auto"
                >
                </v-checkbox>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :class="{ requiredMark: targetId === null }"
                  :rules="{required: !targetId, max: 15, alpha_num_only: true}"
                  name="売上品目コード"
                >
                  <v-text-field
                    :disabled="!!targetId"
                    :filled="!!targetId"
                    v-model="forms.code"
                    :error-messages="errors"
                    dense
                    label="売上品目コード"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>売上品目コード</span>
                      <span class="ml-1">
                        ({{ forms.code?.length || 0 }} / 15)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  rules="required|max:30"
                  class="requiredMark"
                  name="売上品目名"
                >
                  <v-text-field
                    v-model="forms.name"
                    :error-messages="errors"
                    dense
                    label="売上品目名"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>売上品目名</span>
                      <span class="ml-1">
                        ({{ forms.name?.length || 0 }} / 30)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :custom-messages="customMessages"
                  :rules="{
                    double: true, min_value: -9999999.999,
                    max_value: forms.sales_item_type === 'purchase_item' ? 0 : 9999999.999,
                    quantityWithDecimal: { int_digits: 7, dec_digits: 3 }
                  }"
                  name="標準単価"
                >
                  <v-text-field
                    v-model="forms.price"
                    :error-messages="errors"
                    dense
                    label="標準単価"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    reverse
                  />
                </validation-provider>
              </v-col>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  name="単位"
                  :class="( forms.price ) ? 'requiredMark' : ''"
                  :rules="{ required: !!forms.price }"
                >
                  <v-select
                    v-model="forms.sales_item_unit_id"
                    :error-messages="errors"
                    :items="salesItemUnits"
                    clearable
                    dense
                    hide-details="auto"
                    label="単位"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{ required: true }"
                  class="requiredMark"
                  name="課税区分"
                >
                  <v-select
                    v-model="forms.tax_type"
                    :error-messages="errors"
                    :items="taxTypes"
                    clearable
                    dense
                    hide-details="auto"
                    label="課税区分"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{ required: true }"
                  class="requiredMark"
                  name="品目区分"
                >
                  <v-select
                    v-model="forms.sales_item_type"
                    :error-messages="errors"
                    :items="salesItemTypes"
                    dense
                    hide-details="auto"
                    label="品目区分"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :class="forms.sales_item_type === 'jwnet_item' ? 'requiredMark' : ''"
                  :rules="{ required: forms.sales_item_type === 'jwnet_item' }"
                  name="取扱品目"
                >
                  <item-autocomplete
                    v-model="forms.item_id"
                    :items="items"
                    :error-messages="errors"
                    label="取扱品目"
                    clearable
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="品目名で検索する"
                  />
                </validation-provider>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{ max:30 }"
                  name="廃棄物の名称"
                >
                  <v-text-field
                    v-model="forms.waste_name"
                    :error-messages="errors"
                    dense
                    label="廃棄物の名称"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>廃棄物の名称</span>
                      <span class="ml-1">
                        ({{ forms.waste_name?.length || 0 }} / 30)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
              <!-- 荷姿 -->
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  name="荷姿"
                >
                  <v-select
                    v-model="forms.packing_id"
                    :error-messages="errors"
                    :items="packings"
                    clearable
                    dense
                    hide-details="auto"
                    label="荷姿"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
            </v-row>
            <!-- 荷姿 -->
            <!-- 有害物質 -->
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{
                    duplicateKeys: { keys: [
                      forms.hazardous_substance_2_id,
                      forms.hazardous_substance_3_id,
                      forms.hazardous_substance_4_id,
                      forms.hazardous_substance_5_id,
                      forms.hazardous_substance_6_id
                    ] }
                  }"
                  name="有害物質1"
                >
                  <v-select
                    v-model="forms.hazardous_substance_1_id"
                    :disabled="disableHazardousSubstance"
                    :error-messages="errors"
                    :items="hazardousSubstances"
                    clearable
                    dense
                    hide-details="auto"
                    label="有害物質1"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{
                    duplicateKeys: { keys: [
                      forms.hazardous_substance_1_id,
                      forms.hazardous_substance_3_id,
                      forms.hazardous_substance_4_id,
                      forms.hazardous_substance_5_id,
                      forms.hazardous_substance_6_id
                    ] }
                  }"
                  name="有害物質2"
                >
                  <v-select
                    v-model="forms.hazardous_substance_2_id"
                    :disabled="disableHazardousSubstance"
                    :error-messages="errors"
                    :items="hazardousSubstances"
                    clearable
                    dense
                    hide-details="auto"
                    label="有害物質2"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{
                    duplicateKeys: { keys: [
                      forms.hazardous_substance_1_id,
                      forms.hazardous_substance_2_id,
                      forms.hazardous_substance_4_id,
                      forms.hazardous_substance_5_id,
                      forms.hazardous_substance_6_id
                    ] }
                  }"
                  name="有害物質3"
                >
                  <v-select
                    v-model="forms.hazardous_substance_3_id"
                    :disabled="disableHazardousSubstance"
                    :error-messages="errors"
                    :items="hazardousSubstances"
                    clearable
                    dense
                    hide-details="auto"
                    label="有害物質3"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{
                    duplicateKeys: { keys: [
                      forms.hazardous_substance_1_id,
                      forms.hazardous_substance_2_id,
                      forms.hazardous_substance_3_id,
                      forms.hazardous_substance_5_id,
                      forms.hazardous_substance_6_id
                    ] }
                  }"
                  name="有害物質4"
                >
                  <v-select
                    v-model="forms.hazardous_substance_4_id"
                    :disabled="disableHazardousSubstance"
                    :error-messages="errors"
                    :items="hazardousSubstances"
                    clearable
                    dense
                    hide-details="auto"
                    label="有害物質4"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{
                    duplicateKeys: { keys: [
                      forms.hazardous_substance_1_id,
                      forms.hazardous_substance_2_id,
                      forms.hazardous_substance_3_id,
                      forms.hazardous_substance_4_id,
                      forms.hazardous_substance_6_id
                    ] }
                  }"
                  name="有害物質5"
                >
                  <v-select
                    v-model="forms.hazardous_substance_5_id"
                    :disabled="disableHazardousSubstance"
                    :error-messages="errors"
                    :items="hazardousSubstances"
                    clearable
                    dense
                    hide-details="auto"
                    label="有害物質5"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{
                    duplicateKeys: { keys: [
                      forms.hazardous_substance_1_id,
                      forms.hazardous_substance_2_id,
                      forms.hazardous_substance_3_id,
                      forms.hazardous_substance_4_id,
                      forms.hazardous_substance_5_id
                    ] }
                  }"
                  name="有害物質6"
                >
                  <v-select
                    v-model="forms.hazardous_substance_6_id"
                    :disabled="disableHazardousSubstance"
                    :error-messages="errors"
                    :items="hazardousSubstances"
                    clearable
                    dense
                    hide-details="auto"
                    label="有害物質6"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /有害物質 -->
            <!-- 運搬ルート -->
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  name="運搬ルート"
                >
                  <v-input
                    v-model="forms.transport_route_id"
                    :error-messages="errors"
                    dense
                    label="運搬ルート"
                    outlined
                    readonly
                    hide-details="auto"
                  />
                </validation-provider>
                <div class="my-4">
                  <v-text-field
                    v-model="searchByKeyword"
                    append-icon="mdi-magnify"
                    clearable
                    dense
                    hide-details="auto"
                    label="絞り込み検索"
                    outlined
                    placeholder="ルート名・運搬先名・処分先名から検索"
                    persistent-placeholder
                    style="font-size: 90%"
                  >
                    <template v-slot:append-outer>
                      <div>
                        <v-btn
                          class="primary"
                          small
                          @click="clearRootButtonClickEvent"
                        >
                          選択したルートをクリアする
                        </v-btn>
                      </div>
                    </template>
                  </v-text-field>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-data-table
                  :headers="transportRoutesTableHeaders"
                  :items="transportRoutes"
                  :item-class="transportRouteRowBackground"
                  :items-per-page="-1"
                  :search="searchByKeyword"
                  class="elevation-1"
                  disable-sort
                  fixed-header
                  hide-default-footer
                  height="25vh"
                  single-select
                  @click:row="transportRowClick"
                >
                  <template v-slot:[`item.name`]="{ item }">
                    <span :title="item.name">{{ item.name | truncate(20) }}</span>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  name="表示順"
                  rules="numeric|min_value:0|max_value:99999"
                >
                  <v-text-field
                    v-model="forms.sort_priority"
                    :error-messages="errors"
                    :show-size="1000"
                    dense
                    label="表示順"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    reverse
                  />
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /運搬ルート -->
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn @click="closeButtonClickEvent">
              閉じる
            </v-btn>
            <v-btn
              color="primary"
              @click="registerButtonClickEvent"
            >
              登録
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </validation-observer>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import ItemAutocomplete from "@/driver/components/ItemAutocomplete.vue";

export default {
  components: { ItemAutocomplete },
  mixins: [],
  props: {
    enableDialog: {
      type: Boolean,
      default: () => false,
    },
    targetId: {
      type: Number,
      default: () => null,
    },
    /** 取扱品目 */
    items: {
      type: Array,
      default: () => null,
    },
    /** 売上品目単位 */
    salesItemUnits: {
      type: Array,
      default: () => null,
    },
    packings: {
      type: Array,
      default: () => null,
    },
    hazardousSubstances: {
      type: Array,
      default: () => null,
    },
    transportRoutes: {
      type: Array,
      default: () => null,
    },
  },
  data () {
    return {
      inputtedKeyword: null,
      collected_reference_date: this.$dayjs().format("YYYY-MM-DD"),
      /* 課税区分 */
      taxTypes: [],
      salesItemTypes: [],
      forms: {},
      transportRoutesTableHeaders: [
        { value: "name", text: "ルート名称", width: "25%" },
        { value: "transporter_name", text: "区間１収集運搬業者", width: "25%" },
        { value: "trader_name", text: "区間１運搬先会社名", width: "25%" },
        { value: "disposer_location_name", text: "処分業者事業場名", width: "25%" },
      ],
      searchByKeyword: null,
      customMessages: {
        double: '標準単価は半角数字のみ使用できます'
      },
      selectedRouteRow: null,
    }
  },
  computed: {
    disableHazardousSubstance () {
      let item = this.items.find( (v) => v.value === this.forms.item_id)
      if (item === undefined) {
        this.removeHazardousSubstances()
        return true
      }
      if (!item.use_harmful) {
        this.removeHazardousSubstances()
      }
      return !item.use_harmful
    },
  },
  watch: {
    enableDialog (value) {
      this.$nextTick(() => this.$refs.SalesItemPriceFormValidation.reset())
      Object.assign(this.$data.forms, this.$options.data.call(this).forms);
      if (value) {
        // ダイアログ表示時に、運搬ルートリストの前回選択していた行を未選択にする
        if (this.selectedRouteRow) {
          this.selectedRouteRow.select(false)
          this.selectedRouteRow = null
        }
        if (this.targetId) {
          this.searchEditSalesItemPrice()
        } else {
          this.searchNewSalesItemPrice()
        }
      }
    }
  },
  created() {},
  methods: {
    /** api取得 */
    ...mapActions('DriverMasterSalesItemPrice', {
      newSalesItemPrice: 'new',
      editSalesItemPrice: 'edit',
      createSalesItemPrice: 'create',
      updateSalesItemPrice: 'update',
    }),
    /**
     * 新規時表示情報取得
     */
    searchNewSalesItemPrice () {
      this.newSalesItemPrice().then((content) => {
        this.forms = content
        this.taxTypes = content.tax_types
        this.salesItemTypes = content.sales_item_types
      })
    },
    /**
     * 編集時表示情報取得
     */
    searchEditSalesItemPrice () {
      this.editSalesItemPrice({ id: this.targetId }).then((content) => {
        this.forms = content
        this.taxTypes = content.tax_types
        this.salesItemTypes = content.sales_item_types
      })
    },
    /**
     * 閉じるボタン クリックイベント
     */
    closeButtonClickEvent () {
      this.$emit('closeEvent')
    },
    /**
    * 登録ボタン クリックイベント
    */
    registerButtonClickEvent () {
      let $self = this
      this.$refs.SalesItemPriceFormValidation.validate().then((result) => {
        if(result){
          $self.sendRegistrationInformation()
        }
      })
    },
    sendRegistrationInformation(){
      if (this.targetId) {
        this.updateSalesItemPrice({ id: this.targetId, params: this.forms}).then(() => {
          this.$emit('registerEvent')
        })
      } else {
        this.createSalesItemPrice({ params: this.forms }).then(() => {
          this.$emit('registerEvent')
        })
      }
    },
    removeHazardousSubstances () {
      this.forms.hazardous_substance_1_id = null
      this.forms.hazardous_substance_2_id = null
      this.forms.hazardous_substance_3_id = null
      this.forms.hazardous_substance_4_id = null
      this.forms.hazardous_substance_5_id = null
      this.forms.hazardous_substance_6_id = null
    },
    /**
     * 選択したルートをクリアするボタン クリックイベント
     */
    clearRootButtonClickEvent () {
      if (this.selectedRouteRow) {
        this.selectedRouteRow.select(false)
        this.selectedRouteRow = null
      }
      this.forms.transport_route_id = null
    },
    /**
     * 運搬ルート背景色設定
     * @param item
     * @return {string}
     */
    transportRouteRowBackground ( item ) {
      return (item.id === this.forms.transport_route_id) ? 'v-data-table__selected' : ''
    },
    /**
     * 運搬ルート行 クリックイベント
     * @param item
     * @param row
     */
    transportRowClick (item, row) {
      this.forms.transport_route_id = item.id
      row.select(true)
      this.selectedRouteRow = row
    },
    /**
     * 表示基準日変更　コールバック
     */
    referenceDateChangedCallback (value) {
      this.forms.reference_date = value;
    },
  }
}
</script>

<style scoped>
</style>
